import styled from "@emotion/styled";
import { space } from "@smartsheet/design-tokens";
import { sizes } from "../../../constants/spacing";
import { getButtonLoadingStyles } from "../styled/getButtonLoadingStyles";
import { ButtonAppearances, ButtonSizes, LinkAppearances } from "../types";

interface Props {
    appearance: ButtonAppearances | LinkAppearances;
    hasLeftIcon: boolean;
    hasOnlyIcons: boolean;
    hasRightIcon: boolean;
    iconLocation: "before" | "after";
    size: ButtonSizes;
    isLoading?: boolean;
}

const ursaMargin = {
    small: space.space10,
    medium: space.space20,
    large: space.space30,
};

const singleIconMargin = {
    small: `2px`,
    medium: space.space10,
    large: space.space20,
};

const iconsOnlyInsideMargin = {
    small: 1,
    medium: 2,
    large: 2,
};

const getIconWrapperMargin = ({ size, appearance, iconLocation, hasLeftIcon, hasOnlyIcons, hasRightIcon }: Props) => {
    const isTwoIconsOnly = hasLeftIcon && hasRightIcon && hasOnlyIcons;
    const isSingleIcon = hasOnlyIcons && ((hasLeftIcon && !hasRightIcon) || (!hasLeftIcon && hasRightIcon));

    if (isSingleIcon) {
        return `calc(${singleIconMargin[size]} - 1px)`;
    } else {
        switch (appearance) {
            // For Lodestar buttons.
            case "borderless":
            case "primary":
            case "secondary":
            case "destructive":
            case "destructive-tertiary":
            case "secondary-destructive":
            case "share":
                return `0 ${(size === "large" ? sizes.xSmall : sizes.xxSmall) - 1}px`;
            // For Ursa buttons.
            case "primary-filled":
            case "primary-empty":
            case "secondary-filled":
            case "secondary-outlined":
            case "secondary-empty":
            case "canvas-filled":
            case "canvas-empty":
            case "destructive-filled":
            case "destructive-empty":
                if (iconLocation === "before") {
                    return isTwoIconsOnly && size === "small"
                        ? // account for size small outer spacing being different than all other situations
                          `0 1px`
                        : `0 ${sizes.xxSmall - 1}px 0 calc(${ursaMargin[size]} - 1px)`;
                } else {
                    return isTwoIconsOnly && size === "small"
                        ? // account for size small outer spacing being different than all other situations
                          `0 1px`
                        : `0 calc(${ursaMargin[size]} - 1px) 0 ${sizes.xxSmall - 1}px`;
                }
        }
    }
};

export const IconWrapper = styled("div")<Props>`
    align-self: center;
    display: flex;
    flex-shrink: 0;
    line-height: 0;
    font-size: 0;
    user-select: none;
    margin: ${({ size, appearance, iconLocation, hasLeftIcon, hasOnlyIcons, hasRightIcon }) =>
        getIconWrapperMargin({ size, appearance, iconLocation, hasLeftIcon, hasOnlyIcons, hasRightIcon })};

    // inner margin overrides for double icons with no text
    margin-left: ${({ hasLeftIcon, hasOnlyIcons, iconLocation, size }) =>
        hasLeftIcon && hasOnlyIcons && iconLocation === "after" ? `${iconsOnlyInsideMargin[size]}px` : null};
    margin-right: ${({ hasRightIcon, hasOnlyIcons, iconLocation, size }) =>
        hasRightIcon && hasOnlyIcons && iconLocation === "before" ? `${iconsOnlyInsideMargin[size]}px` : null};

    ${({ isLoading }) => getButtonLoadingStyles(isLoading)}
`;
